import { Instructions } from "../components/pages/checklist/Instructions.js";
import {
  Checklist,
  type ChecklistQuestion,
} from "../components/pages/checklist/Checklist.js";
import { useCallback, useState } from "react";
import { ChecklistService, type ChecklistInstanceAnswer } from "../services/checklistService.js";
import { useMutation } from "@tanstack/react-query";
import { Button } from "../components/ui/Button.js";
import { PageHeader } from "../components/pages/PageHeader.js";
import { useNavigate } from "react-router-dom";
import { useLabStages } from "../hooks/useLabStages.js";

interface ChecklistPageProps {
  certificationId: number;
  instanceId: number;
  labType: "dry" | "wet";
  tier: "bronze" | "silver" | "gold";
  checklists: ChecklistQuestion[];
  labId: string;
  answers: ChecklistInstanceAnswer[];
  checklistId: string;
  auditorMode?: boolean;
  isSubmitted?: boolean;
}

/**
 * Renders one certification checklist: the tier instructions, the questions
 * themselves, and the submit/lock flow.
 *
 * Note: the auditor mode/version of this checklist is not implemented yet.
 * Currently waiting for some backend data to populate the "no" suggested actions.
 */
export const ChecklistPage = ({
  certificationId,
  instanceId,
  labType,
  tier,
  checklists,
  labId,
  answers,
  checklistId,
  auditorMode,
  isSubmitted = false,
}: ChecklistPageProps) => {
  const navigate = useNavigate();

  const { data: stages } = useLabStages(labId, tier);
  const preview = !stages || stages.checklist.actions === "preview";

  // Null while loading: stay read-only, but don't flash a warning about a state
  // we haven't confirmed yet.
  const previewReason = !stages
    ? null
    : stages.baselineProgressionEnabled
      ? "Complete and lock every baseline assessment for this tier to start editing this checklist."
      : "This checklist is available to view. Responses can't be saved or submitted yet.";

  const [isAuditorMode, setIsAuditorMode] = useState(auditorMode || false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [submitMessage, setSubmitMessage] = useState<string>('');
  const [persistentError, setPersistentError] = useState<string>('');

  const submitChecklistMutation = useMutation({
    mutationFn: (comments?: string) =>
      ChecklistService.submitChecklistInstance(certificationId, instanceId, comments),
    onSuccess: () => {
      setSubmitStatus('success');
      setSubmitMessage('Checklist submitted successfully! Redirecting to dashboard...');
      // Redirect to Lab Dashboard after successful submission
      setTimeout(() => {
        setSubmitStatus('idle');
        navigate(`/lab/${labId}`, { replace: true });
      }, 1500); // Brief delay to show success message
    },
    onError: (error: unknown) => {
      setSubmitStatus('error');
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit checklist';
      setSubmitMessage(`Submission failed: ${errorMessage}`);
      setPersistentError(`Submission failed: ${errorMessage}`);
    },
  });

  const checklistSubmit = useCallback((comments?: string) => {
    setSubmitStatus('loading');
    setSubmitMessage('Submitting checklist...');
    setPersistentError('');
    submitChecklistMutation.mutate(comments);
  }, [submitChecklistMutation]);

  return (
    <div className="l0-layout">
      <PageHeader
        pageTitle={getPageTitle(labType)}
        backButton={
          <Button
            variant="neutral"
            small
            onClick={() => {
              navigate(-1);
            }}
          >
            Back
          </Button>
        }
      />

      {/* Preview Mode Alert - Sticky at top */}
      {preview && previewReason && (
        <div className="alert bg-orange-50 mb-6 border-2 border-solid border-[#eaddc9] rounded-lg shadow-md sticky top-0 z-50 mt-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <h3 className="font-bold text-orange-900">Preview Mode</h3>
            <div className="text-base font-medium text-orange-900">
              {previewReason}
            </div>
          </div>
        </div>
      )}

      <Instructions
        className="my-8"
        labType={labType}
        tier={tier}
        auditorMode={auditorMode}
        isAuditorMode={isAuditorMode}
        isActualAuditor={auditorMode}
      />
      <Checklist
        checklistId={checklistId}
        labId={labId}
        questions={checklists}
        onSubmit={checklistSubmit}
        previewMode={preview}
        auditorMode={auditorMode}
        labType={labType}
        tier={tier}
        certificationId={certificationId}
        instanceId={instanceId}
        answers={answers}
        isSubmitted={isSubmitted}
        isAuditorMode={isAuditorMode}
        setIsAuditorMode={setIsAuditorMode}
      />

      <div className="mt-4">
        {/* Submission Status Messages */}
        {submitStatus === 'success' && (
          <div className="alert alert-success mb-6 rounded-xl">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <div className="font-semibold">Success!</div>
              <div className="text-sm">{submitMessage}</div>
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="alert alert-error mb-6 rounded-xl">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <div className="font-semibold">Error</div>
              <div className="text-sm">{persistentError || submitMessage}</div>
              <div className="text-xs mt-2 opacity-75">
                Please address the issues above and try submitting again.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const getPageTitle = (type: string): string => {
  switch (type) {
    case "dry":
      return "Dry Lab Checklist";
    case "wet":
      return "Wet Lab Checklist";
    default:
      return "Lab Checklist";
  }
};
